import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';

const familyOptions = ['2人', '3人', '4人以上'];
const housingOptions = ['商品房', '自建房', '保障房', '租房'];
const economicOptions = ['收入1000', '收入3000', '收入10000以上'];
const relationOptions = ['配偶', '父亲', '母亲', '儿子', '女儿', '兄弟', '姐妹', '其他'];

interface FamilyMember {
  relation: string;
  name: string;
  gender: string;
  birthday: string;
  id_number: string;
}

function getFamilyCount(opt: string): number {
  if (opt === '2人') return 1;
  if (opt === '3人') return 2;
  if (opt === '4人以上') return 3;
  return 0;
}

export default function HousingClaim() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [idCard, setIdCard] = useState('');
  const [address, setAddress] = useState('');
  const [family, setFamily] = useState('');
  const [familyDetails, setFamilyDetails] = useState<FamilyMember[]>([]);
  const [housing, setHousing] = useState('');
  const [economic, setEconomic] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [checking, setChecking] = useState(true);
  const [eligible, setEligible] = useState(false);
  const [inviteCount, setInviteCount] = useState(0);

  useEffect(() => {
    Promise.all([
      api.get('/housing-application/status'),
      api.get('/invite-reward-tiers'),
    ]).then(([statusRes, tierRes]) => {
      if (statusRes.data.has_applied) {
        navigate('/property-status', { replace: true });
        return;
      }
      const count = tierRes.data?.current_invites ?? 0;
      setInviteCount(count);
      setEligible(count >= 3);
    }).catch(() => { }).finally(() => setChecking(false));
  }, [navigate]);

  function handleFamilySelect(opt: string) {
    setFamily(opt);
    const count = getFamilyCount(opt);
    const newDetails: FamilyMember[] = [];
    for (let i = 0; i < count; i++) {
      newDetails.push(familyDetails[i] || { relation: '', name: '', gender: '', birthday: '', id_number: '' });
    }
    setFamilyDetails(newDetails);
  }

  function updateFamilyMember(index: number, field: keyof FamilyMember, value: string) {
    setFamilyDetails(prev => prev.map((m, i) => i === index ? { ...m, [field]: value } : m));
  }

  async function handleSubmit() {
    if (submitting) return;
    if (!name.trim() || !idCard.trim() || !address.trim() || !family || !housing || !economic) {
      toast.error('请填写完整信息');
      return;
    }
    if (familyDetails.some(m => !m.relation || !m.name || !m.gender || !m.birthday || !m.id_number)) {
      toast.error('请填写完整家庭成员信息');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/housing-application', {
        name: name.trim(),
        id_card: idCard.trim(),
        address: address.trim(),
        family_members: family,
        family_details: familyDetails.length > 0 ? familyDetails : undefined,
        housing_status: housing,
        economic_status: economic,
      });
      toast.success('提交成功');
      navigate('/property-status', { replace: true });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '提交失败');
    } finally {
      setSubmitting(false);
    }
  }

  if (checking) return <div className="max-w-md mx-auto min-h-screen bg-white" />;

  if (!eligible) {
    return (
      <div className="max-w-md mx-auto min-h-screen bg-white">
        <AuthGuard />
        <div className="relative">
          <img src="/images/lingqv_sang.png" alt="" className="w-full" />
          <button
            onClick={() => navigate(-1)}
            className="absolute top-3 left-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/20 text-white"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
          </button>
        </div>
        <div className="px-6 py-10 text-center relative z-10 mt-[-200px]">
          <div className="text-5xl mb-4">🔒</div>
          <h3 className="text-lg font-bold text-gray-800 mb-2">暂未开放</h3>
          <p className="text-sm text-gray-500 mb-1">需要邀请至少 <span className="text-[#c4161c] font-bold">3</span> 位成员完成实名认证</p>
          <p className="text-sm text-gray-500 mb-6">当前已邀请：<span className="text-[#c4161c] font-bold">{inviteCount}</span> / 3</p>
          <button
            onClick={() => navigate('/invite')}
            className="bg-[#c4161c] text-white px-8 py-2.5 rounded-full text-sm font-bold"
          >
            去邀请好友
          </button>
        </div>
        <div className="mt-[-220px]">
          <img src="/images/lingqv_xia.png" alt="" className="w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto min-h-screen bg-white">
      <AuthGuard />

      {/* Hero */}
      <div className="relative">
        <img src="/images/lingqv_sang.png" alt="" className="w-full" />
        <button
          onClick={() => navigate(-1)}
          className="absolute top-3 left-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/20 text-white"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
      </div>

      {/* Form header */}
      <div className="px-4 -mt-2 relative z-10 flex items-center justify-center mt-[-180px]">
        <img src="/images/lingqv_biaoge.png" alt="填写表格" width={250} />
      </div>

      {/* Form */}
      <div
        className="mx-3 mt-1 relative z-10 p-4 space-y-4"
        style={{
          backgroundImage: 'url(/images/lingqv_kuang.png)',
          backgroundSize: '100% 100%',
        }}
      >
        {/* Name */}
        <div className="flex gap-2">
          <div className="flex items-center gap-2 mb-1.5 w-[120px]">
            <img src="/images/lingqv_xingming.png" alt="" className="w-5 h-5" />
            <span className="text-[11px] font-medium text-gray-800">姓名：</span>
          </div>
          <input
            type="text"
            placeholder="请输入姓名"
            value={name}
            onChange={e => setName(e.target.value)}
            className="w-full h-9 px-3 rounded-md border border-gray-200 bg-white text-xs outline-none"
          />
        </div>

        {/* ID Card */}
        <div className="flex gap-2">
          <div className="flex items-center gap-2 mb-1.5 w-[120px]">
            <img src="/images/lingqv_shenfenzhneg.png" alt="" className="w-5 h-5" />
            <span className="text-[11px] font-medium text-gray-800">身份证：</span>
          </div>
          <input
            type="text"
            placeholder="请输入身份证号码"
            value={idCard}
            onChange={e => setIdCard(e.target.value)}
            className="w-full h-9 px-3 rounded-md border border-gray-200 bg-white text-xs outline-none"
          />
        </div>

        {/* Address */}
        <div>
          <div className="flex items-start gap-2 mb-1.5">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="#c4161c" stroke="none"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 010-5 2.5 2.5 0 010 5z" /></svg>
            <div className="flex flex-col gap-2 w-full">
              <span className="text-[11px] font-medium text-gray-800">居住地详细地址</span>
              <textarea
                placeholder="请输入居住地详细地址"
                value={address}
                onChange={e => setAddress(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 rounded-md border border-gray-200 bg-white text-xs outline-none resize-none"
              />
            </div>
          </div>
        </div>

        <img src="/images/lingqv_henxgian.png" alt="" className="w-full" />

        {/* Family Members */}
        <div className="flex gap-2 justify-start">
          <div className="flex items-center gap-2 mb-1.5 w-[120px]">
            <img src="/images/lingqv_chenyuan.png" alt="" className="w-5 h-5" />
            <span className="text-[11px] font-medium text-gray-800">家族成员：</span>
          </div>
          <div className="flex gap-2 w-full flex-wrap">
            {familyOptions.map(opt => (
              <button
                key={opt}
                onClick={() => handleFamilySelect(opt)}
                className={`flex-1 px-2 py-2 rounded-md text-[11px] border transition ${family === opt ? 'bg-[#c4161c] text-white border-[#c4161c]' : 'bg-white text-gray-700 border-gray-200'}`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        {/* Family Detail Forms */}
        {familyDetails.length > 0 && (
          <div className="space-y-3">
            {familyDetails.map((member, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-3 space-y-2 bg-white/80">
                <div className="text-[11px] font-bold text-[#c4161c] mb-1">家庭成员 {idx + 1}</div>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    value={member.relation}
                    onChange={e => updateFamilyMember(idx, 'relation', e.target.value)}
                    className="h-8 px-2 rounded-md border border-gray-200 bg-white text-[11px] outline-none"
                  >
                    <option value="">与申请人关系</option>
                    {relationOptions.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <input
                    type="text"
                    placeholder="姓名"
                    value={member.name}
                    onChange={e => updateFamilyMember(idx, 'name', e.target.value)}
                    className="h-8 px-2 rounded-md border border-gray-200 bg-white text-[11px] outline-none"
                  />
                  <select
                    value={member.gender}
                    onChange={e => updateFamilyMember(idx, 'gender', e.target.value)}
                    className="h-8 px-2 rounded-md border border-gray-200 bg-white text-[11px] outline-none"
                  >
                    <option value="">性别</option>
                    <option value="男">男</option>
                    <option value="女">女</option>
                  </select>
                  <input
                    type="date"
                    placeholder="出生日期"
                    value={member.birthday}
                    onChange={e => updateFamilyMember(idx, 'birthday', e.target.value)}
                    className="h-8 px-2 rounded-md border border-gray-200 bg-white text-[11px] outline-none"
                  />
                </div>
                <input
                  type="text"
                  placeholder="公民身份证号"
                  value={member.id_number}
                  onChange={e => updateFamilyMember(idx, 'id_number', e.target.value)}
                  className="w-full h-8 px-2 rounded-md border border-gray-200 bg-white text-[11px] outline-none"
                />
              </div>
            ))}
          </div>
        )}

        {/* Housing Status */}
        <div className="flex gap-2 justify-start">
          <div className="flex items-center gap-2 mb-1.5 w-[120px]">
            <img src="/images/lingqv_zhufnag.png" alt="" className="w-5 h-5" />
            <span className="text-[11px] font-medium text-gray-800">住房现状：</span>
          </div>
          <div className="flex gap-2 flex-wrap w-full">
            {housingOptions.map(opt => (
              <button
                key={opt}
                onClick={() => setHousing(opt)}
                className={`flex-1 px-1 py-2 rounded-md text-[11px] border transition ${housing === opt ? 'bg-[#c4161c] text-white border-[#c4161c]' : 'bg-white text-gray-700 border-gray-200'}`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        {/* Economic Status */}
        <div className="flex gap-1">
          <div className="flex items-center gap-1 mb-1.5 w-[120px]">
            <img src="/images/lingqv_hzuangfai.png" alt="" className="w-5 h-5" />
            <span className="text-[11px] font-medium text-gray-800">经济状况：</span>
          </div>
          <div className="flex gap-2 flex-wrap w-full">
            {economicOptions.map(opt => (
              <button
                key={opt}
                onClick={() => setEconomic(opt)}
                className={`flex-1 px-0.5 py-2 rounded-lg text-[9px] border transition ${economic === opt ? 'bg-[#c4161c] text-white border-[#c4161c]' : 'bg-white text-gray-700 border-gray-200'}`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Submit */}
      <div className="px-4 mt-4 relative z-10">
        <button onClick={handleSubmit} disabled={submitting} className="w-full disabled:opacity-60 flex justify-center">
          <img src="/images/lingqv_queren.png" alt="确认提交" width={330} />
        </button>
        <p className="text-center text-[11px] text-gray-400 mt-2 mb-1">
          请确保填写信息真实有效，我们将严格保密您的个人信息
        </p>
      </div>

      {/* Footer */}
      <div className="mt-[-300px]">
        <img src="/images/lingqv_xia.png" alt="" className="w-full" />
      </div>
    </div>
  );
}
